import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { ChevronDown, ChevronRight, PencilFill, GripVertical } from 'react-bootstrap-icons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';

import useLocalization from '../../hooks/useLocalization';
import { isAdmin, statusToColorMap } from '../../helpers/global';
import useAuth from '../../hooks/useAuth';
import { Badge } from 'react-bootstrap';
import { snakeCase } from 'lodash';
import StatusGroupTable from './StatusGroupTable';
import { useCallback } from 'react';
import { statusOrderServices } from '../../services/statusOrderServices';

const GroupStatus = ({
  rowKey = '_id',
  onRowClick,
  maxTableHeight = '100vh',
  users = [],
  setSelectedApartment,
  selectedApartment,
  showMoreStatuses = false,
  apartmentProfile = null,
  showArchived = false,
  getTableFilterValues,
  statusTableTriggerAction,
  setStatusTableTriggerAction,
  statusOrderRef
}) => {
  const { translate } = useLocalization();
  const { user } = useAuth();
  const [statusSelectionInfo, setStatusSelectionInfo] = useState({});

  // Initialize all groups as collapsed by default
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    return statusOrderRef.current.reduce((acc, status) => {
      acc[status] = true;
      return acc;
    }, {});
  });
  console.log('collapsedGroups', collapsedGroups);

  const handleToggleGroup = status => {
    setCollapsedGroups(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  const handleHeaderSelectionInfoChange = useCallback((status, info) => {
    setStatusSelectionInfo(prev => ({
      ...prev,
      [status]: info
    }));
  }, []);

  // Handle drag end - reorder the status array
  const handleDragEnd = async result => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    const previousOrder = [...statusOrderRef.current];
    const newOrder = [...previousOrder];
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(destinationIndex, 0, removed);

    // optimistic update
    statusOrderRef.current = newOrder;

    // Create or update status order on server
    const { response, error } = await statusOrderServices.createOrUpdateStatusOrder({
      statusOrder: newOrder
    });

    if (error) {
      toast.error(error);
      statusOrderRef.current = previousOrder;
      return;
    }

    if (response) {
      statusOrderRef.current = response.statusOrder;
      toast.success('Status order updated successfully');
    }
  };

  const renderGroupHeader = (status, isCollapsed, provided) => {
    const selectionInfo = statusSelectionInfo[status] || {};
    const {
      selectAllMode,
      selectedItems,
      unselectedItems,
      pageInfo,
      showMultiEditSidebar,
      setShowMultiEditSidebar
    } = selectionInfo;

    return (
      <div
        className={`d-flex align-items-center justify-content-between group-status-header ${
          isCollapsed ? 'collapsed' : 'expanded'
        }`}
        role="button"
        tabIndex={0}
        onClick={() => handleToggleGroup(status)}
      >
        <div className="d-flex align-items-center gap-3" style={{ flex: 1 }}>
          <div
            {...provided?.dragHandleProps}
            className="d-flex align-items-center justify-content-center group-status-drag-handle"
            onClick={e => e.stopPropagation()}
            style={{ cursor: 'grab', padding: '4px' }}
          >
            <GripVertical width={16} height={16} className="text-muted" />
          </div>
          <div className="d-flex align-items-center justify-content-center group-status-chevron">
            {isCollapsed ? <ChevronRight width={18} height={18} /> : <ChevronDown width={18} height={18} />}
          </div>
          <Badge className="group-status-badge" bg={statusToColorMap[status] || 'primary text-white'}>
            {'\u00A0'}
          </Badge>
          <span className="group-status-text">{translate(snakeCase(status))}</span>
        </div>
        <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
          {!isCollapsed && (
            <>
              {isAdmin(user?.role) && pageInfo && (
                <Button
                  className="d-flex justify-content-between align-items-center group-status-count-btn"
                  variant="dark"
                  size="sm"
                >
                  <span className="text-white">
                    {`${
                      selectAllMode
                        ? pageInfo?.resultCount - (unselectedItems?.length ?? 0)
                        : selectedItems?.length ?? 0
                    } / ${pageInfo?.resultCount ?? 0} ${translate('apartments')}`}
                  </span>
                </Button>
              )}
              <Button
                variant={showMultiEditSidebar ? 'dark text-white' : 'outline-dark'}
                onClick={e => {
                  e.stopPropagation();
                  if (setShowMultiEditSidebar) {
                    setShowMultiEditSidebar(prev => !prev);
                  }
                }}
                className={`d-flex align-items-center justify-content-center group-status-edit-btn ${
                  showMultiEditSidebar ? 'active' : ''
                }`}
                size="sm"
              >
                <PencilFill size={16} />
              </Button>
            </>
          )}
        </div>
        {!isCollapsed && <div className="group-status-gradient-border" />}
      </div>
    );
  };

  return (
    <div className="position-relative w-100 overflow-auto group-status-container" style={{ maxHeight: maxTableHeight }}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="status-groups">
          {(provided, snapshot) => (
            <div className="d-flex flex-column group-status-list" ref={provided.innerRef} {...provided.droppableProps}>
              {statusOrderRef.current.map((status, index) => {
                const isCollapsed = collapsedGroups[status] === true;

                return (
                  <Draggable key={status} draggableId={status} index={index}>
                    {(provided, snapshot) => {
                      const isDragging = snapshot.isDragging;
                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`group-status-card ${isCollapsed ? 'collapsed' : 'expanded'}`}
                          style={{
                            ...provided.draggableProps.style,
                            ...(isDragging && {
                              backgroundColor: 'white',
                              zIndex: 1000,
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              minHeight: '50px',
                              overflow: 'hidden'
                            })
                          }}
                        >
                          {renderGroupHeader(status, isCollapsed, provided, snapshot)}
                          {!isCollapsed && (
                            <div className="group-status-table-container">
                              <StatusGroupTable
                                status={status}
                                rowKey={rowKey}
                                onRowClick={onRowClick}
                                users={users}
                                setSelectedApartment={setSelectedApartment}
                                selectedApartment={selectedApartment}
                                showMoreStatuses={showMoreStatuses}
                                apartmentProfile={apartmentProfile}
                                showArchived={showArchived}
                                getTableFilterValues={getTableFilterValues}
                                statusTableTriggerAction={statusTableTriggerAction}
                                setStatusTableTriggerAction={setStatusTableTriggerAction}
                                onHeaderSelectionInfoChange={handleHeaderSelectionInfoChange}
                              />
                            </div>
                          )}
                        </div>
                      );
                    }}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default GroupStatus;
